import type { SelectedOptions, SelectedOptionExpanded } from '../types';
import {
  Trash,
  CopySimple,
  Eye,
  Archive,
  ArrowArcLeft,
  PencilSimple,
  X,
} from '@phosphor-icons/react';
import { Trans } from '@lingui/macro';
import { Button, Tooltip } from 'components';
import { useTableContext } from 'features/misc/table/providers/TableProvider';

type SelectedOptionsProps = {
  options: SelectedOptionExpanded[];
  onClick: (selectedOption: SelectedOptions) => void;
};

type ButtonProps = {
  type: SelectedOptions;
  tooltip: string;
  disabled: boolean;
  onClick: (selectedOption: SelectedOptions) => void;
  color?: string;
};

// Export action definitions for use in other components (e.g., createColumns.tsx)
export const btnText = {
  delete: <Trans>Delete</Trans>,
  copy: <Trans>Copy</Trans>,
  read: <Trans>Mark as Read</Trans>,
  unread: <Trans>Mark as Unread</Trans>,
  archive: <Trans>Archive</Trans>,
  restore: <Trans>Restore</Trans>,
  edit: <Trans>Edit</Trans>,
  cancel: <Trans>Cancel</Trans>,
};

export const BtnIcon = {
  delete: Trash,
  copy: CopySimple,
  read: Eye,
  unread: Eye,
  archive: Archive,
  restore: ArrowArcLeft,
  edit: PencilSimple,
  cancel: X,
};

const HeaderButton = ({
  type,
  tooltip,
  disabled,
  onClick,
  color,
  'data-testid': dataTestId,
}: ButtonProps & { 'data-testid'?: string }) => {
  if (!type || !btnText[type]) {
    return null;
  }
  const IconComponent = BtnIcon[type];
  return (
    <Tooltip title={tooltip}>
      <div>
        <Button
          data-testid={dataTestId}
          variant="tertiary"
          size="lg"
          disabled={disabled}
          onClick={() => onClick(type)}
          id={`btn${type}`}
          startIcon={<IconComponent data-testid={`${dataTestId}_icon`} color={color} size={20} />}
        >
          {btnText[type]}
        </Button>
      </div>
    </Tooltip>
  );
};

export const Selected = ({ options, onClick }: SelectedOptionsProps) => {
  const { table, config } = useTableContext();
  const selectedRows = table.getSelectedRowModel()?.flatRows;
  const rowSelectionIds = Object.keys(table.getState().rowSelection);
  return (
    <>
      {options.map(option => {
        const key = typeof option === 'string' ? option : option?.id;
        const disabled =
          typeof option === 'string' ? false : option?.disabled?.(selectedRows, rowSelectionIds);
        const hidden =
          typeof option === 'string' ? false : option?.hidden?.(selectedRows, rowSelectionIds);
        const tooltip =
          typeof option === 'string'
            ? ''
            : typeof option?.tooltip === 'string'
              ? option?.tooltip
              : option?.tooltip?.(selectedRows, rowSelectionIds);
        const color =
          typeof option === 'string'
            ? undefined
            : option?.disabled?.(selectedRows, rowSelectionIds)
              ? '#CBCCCE'
              : undefined;
        if (hidden) {
          return null;
        }

        return (
          <HeaderButton
            data-testid={`table_${config?.tableId}_header_button_${
              typeof option === 'string' ? option : option?.id
            }`}
            key={key}
            type={key}
            tooltip={tooltip}
            disabled={disabled}
            onClick={onClick}
            color={color}
          />
        );
      })}
    </>
  );
};
